import { useState, useCallback, useRef } from 'react';
import { useMutation, useApolloClient } from '@apollo/client';
import { CreateSessionJudgeAssignment } from '../../../../apollo/mutations/CreateSessionJudgeAssignment.graphql';
import { CreateStageJudge } from '../../../../apollo/mutations/CreateStageJudge.graphql';
import { DeleteSessionJudgeAssignment } from '../../../../apollo/mutations/DeleteSessionJudgeAssignment.graphql';
import { DeleteStageJudge } from '../../../../apollo/mutations/DeleteStageJudge.graphql';
import { SearchJudges } from '../../../../apollo/queries/SearchJudges.graphql';
import { GetFullSession } from '../../../../apollo/queries/GetFullSession.graphql';
import { GenderType } from '../../../../models';
import { useEvalConfig } from '../../hooks';
import { getJudgePanel } from '../../../../redux/_constants/evaluator.constants';

// Map apparatus short codes to full names
const apparatusCodeToName = {
  VT: 'Vault',
  UB: 'Uneven Bars',
  BB: 'Balance Beam',
  FX: 'Floor Exercise',
  PH: 'Pommel Horse',
  SR: 'Still Rings',
  PB: 'Parallel Bars',
  HB: 'Horizontal Bar',
};

/**
 * Hook for managing judge assignments from the scoresheet.
 * Handles creating/deleting SessionJudgeAssignment and StageJudge records.
 *
 * @param {Object} session - The session object with rotations and stages
 * @param {Function} onUpdate - Callback when assignments change (for refetching)
 * @param {string} gender - The gender type (MALE/FEMALE) for filtering judges
 */
export function useJudgeAssignment(session, onUpdate, gender) {
  // Note: onUpdate param kept for backwards compatibility but no longer used
  // Cache updates happen directly in mutation callbacks
  const client = useApolloClient();
  const evalConfig = useEvalConfig();
  const judgePanel = getJudgePanel(evalConfig);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [createSessionJudgeAssignment] = useMutation(
    CreateSessionJudgeAssignment
  );
  const [createStageJudge] = useMutation(CreateStageJudge);
  const [deleteSessionJudgeAssignment] = useMutation(
    DeleteSessionJudgeAssignment
  );
  const [deleteStageJudge] = useMutation(DeleteStageJudge);

  // Track in-flight SessionJudgeAssignment creates to prevent duplicates
  // when multiple parallel assignJudge calls happen for the same judge
  const pendingAssignmentCreates = useRef(new Map());

  /**
   * Assign a judge to a specific apparatus with the given score type.
   * Creates SessionJudgeAssignment + StageJudge for stages matching the apparatus.
   *
   * @param {Object} judge - The judge object from search
   * @param {string} scoreType - The score type (J1, J2, etc.)
   * @param {string} apparatus - The apparatus code (VT, UB, BB, FX, etc.)
   * @param {Object} options - { skipUpdate: boolean } - skip triggering refetch (for bulk operations)
   * @returns {Promise<string>} - The created SessionJudgeAssignment ID
   */
  const assignJudge = useCallback(
    async (judge, scoreType, apparatus, { skipUpdate = false } = {}) => {
      if (!session?.id || !judge?.id) {
        throw new Error('Session and judge are required');
      }

      if (!apparatus) {
        throw new Error('Apparatus is required');
      }

      setLoading(true);
      setError(null);

      try {
        // Step 1: Check if judge is already assigned to session, otherwise create
        // Check both field names for compatibility (judgeAssignments from GraphQL, sessionJudgeAssignments legacy)
        const allAssignments =
          session?.judgeAssignments?.items ||
          session?.sessionJudgeAssignments?.items ||
          [];
        let sessionJudgeAssignmentId;
        const existingAssignment = allAssignments.find(
          (a) => a.judgeId === judge.id && !a._deleted
        );

        if (existingAssignment) {
          sessionJudgeAssignmentId = existingAssignment.id;
        } else if (pendingAssignmentCreates.current.has(judge.id)) {
          // Another parallel call is already creating this assignment - await it
          sessionJudgeAssignmentId = await pendingAssignmentCreates.current.get(
            judge.id
          );
        } else {
          // Create new assignment and store the promise for dedup
          const createPromise = createSessionJudgeAssignment({
            variables: {
              input: {
                sessionId: session.id,
                judgeId: judge.id,
              },
            },
          }).then((result) => result.data.createSessionJudgeAssignment.id);

          pendingAssignmentCreates.current.set(judge.id, createPromise);
          try {
            sessionJudgeAssignmentId = await createPromise;
          } finally {
            pendingAssignmentCreates.current.delete(judge.id);
          }
        }

        // Step 2: Remove any existing StageJudge for this scoreType slot, then create new one
        const rotations = session.rotations?.items || [];
        const deletePromises = [];
        const createPromises = [];

        for (const rotation of rotations) {
          const stages = rotation.stages?.items || [];
          for (const stage of stages) {
            // Only process stages matching the specific apparatus
            if (stage.apparatus === apparatus && !stage._deleted) {
              // First, find and remove any existing judge in this slot (same scoreType)
              const existingJudges = stage.judges?.items || [];
              for (const existingJudge of existingJudges) {
                if (
                  !existingJudge._deleted &&
                  existingJudge.scoreTypes?.includes(scoreType)
                ) {
                  const stageId = stage.id;
                  const judgeId = existingJudge.id;
                  deletePromises.push(
                    deleteStageJudge({
                      variables: {
                        input: {
                          id: judgeId,
                          _version: existingJudge._version,
                        },
                      },
                      update: (cache) => {
                        cache.modify({
                          id: cache.identify({
                            __typename: 'Stage',
                            id: stageId,
                          }),
                          fields: {
                            judges: (existingJudgesRef, { readField }) => {
                              if (!existingJudgesRef?.items)
                                return existingJudgesRef;
                              return {
                                ...existingJudgesRef,
                                items: existingJudgesRef.items.filter(
                                  (ref) => readField('id', ref) !== judgeId
                                ),
                              };
                            },
                          },
                        });
                      },
                    }).catch(() => {}) // Ignore delete errors (already deleted, etc.)
                  );
                }
              }

              // Queue create for after deletes
              createPromises.push({
                rotation,
                stage,
                sessionJudgeAssignmentId,
                scoreType,
              });
            }
          }
        }

        // Wait for all deletes to complete first
        await Promise.all(deletePromises);

        // Now create the new StageJudge records
        const stageJudgePromises = createPromises.map(
          ({
            rotation,
            stage,
            sessionJudgeAssignmentId: assignmentId,
            scoreType: st,
          }) =>
            createStageJudge({
              variables: {
                input: {
                  sessionId: session.id,
                  rotationId: rotation.id,
                  stageId: stage.id,
                  sessionJudgeAssignmentId: assignmentId,
                  scoreTypes: [st],
                },
              },
              // Update Apollo cache directly with the new judge data
              update: (cache, { data }) => {
                if (!data?.createStageJudge?.stage) return;

                const stageId = stage.id;
                const newJudges = data.createStageJudge.stage.judges;

                // Update the Stage's judges in the cache
                cache.modify({
                  id: cache.identify({ __typename: 'Stage', id: stageId }),
                  fields: {
                    judges: () => newJudges,
                  },
                });
              },
            })
        );

        await Promise.all(stageJudgePromises);

        // No refetch needed - cache is updated directly by mutation update callbacks

        return sessionJudgeAssignmentId;
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [session, createSessionJudgeAssignment, createStageJudge]
  );

  /**
   * Remove a judge from the session and all their stage assignments.
   *
   * @param {string} sessionJudgeAssignmentId - The SessionJudgeAssignment ID
   * @param {number} _version - The version for optimistic concurrency
   * @param {Object} options - { skipUpdate: boolean } - skip triggering refetch (for bulk operations)
   */
  const removeJudge = useCallback(
    async (sessionJudgeAssignmentId, _version, { skipUpdate = false } = {}) => {
      if (!sessionJudgeAssignmentId) {
        throw new Error('SessionJudgeAssignment ID is required');
      }

      setLoading(true);
      setError(null);

      try {
        // Step 1: Find and delete all StageJudge records for this assignment
        const rotations = session.rotations?.items || [];
        const deletePromises = [];

        for (const rotation of rotations) {
          const stages = rotation.stages?.items || [];
          for (const stage of stages) {
            const judges = stage.judges?.items || [];
            for (const stageJudge of judges) {
              if (
                stageJudge.sessionJudgeAssignmentId ===
                  sessionJudgeAssignmentId &&
                !stageJudge._deleted
              ) {
                const stageId = stage.id;
                const judgeId = stageJudge.id;
                deletePromises.push(
                  deleteStageJudge({
                    variables: {
                      input: {
                        id: judgeId,
                        _version: stageJudge._version,
                      },
                    },
                    // Update Apollo cache to remove the deleted judge
                    update: (cache) => {
                      cache.modify({
                        id: cache.identify({
                          __typename: 'Stage',
                          id: stageId,
                        }),
                        fields: {
                          judges: (existingJudges, { readField }) => {
                            if (!existingJudges?.items) return existingJudges;
                            return {
                              ...existingJudges,
                              items: existingJudges.items.filter(
                                (judgeRef) =>
                                  readField('id', judgeRef) !== judgeId
                              ),
                            };
                          },
                        },
                      });
                    },
                  })
                );
              }
            }
          }
        }

        await Promise.all(deletePromises);

        // Step 2: Delete the SessionJudgeAssignment
        await deleteSessionJudgeAssignment({
          variables: {
            input: {
              id: sessionJudgeAssignmentId,
              _version,
            },
          },
        });

        // No refetch needed - cache is updated directly by mutation update callbacks
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [session, deleteStageJudge, deleteSessionJudgeAssignment]
  );

  /**
   * Bulk assign judges from a parsed table.
   * Searches for each judge by name and assigns them to the appropriate apparatus/slot.
   *
   * @param {Array} assignments - Array of { name, apparatus, judgeIndex } objects
   * @param {Function} onProgress - Callback for progress updates
   * @param {Object} options - { dryRun, searchOnly, judgeOverrides }
   *   - dryRun: Don't actually assign, just simulate
   *   - searchOnly: Stop after search phase (for user to resolve not_found)
   *   - judgeOverrides: Map of "name" -> judge object (for user-selected alternatives)
   * @returns {Promise<Object>} - Results with { success: [...], failed: [...], needsResolution: boolean }
   */
  const bulkAssignJudges = useCallback(
    async (
      assignments,
      onProgress,
      { dryRun = false, searchOnly = false, judgeOverrides = {} } = {}
    ) => {
      if (!session?.id || !assignments?.length) {
        return { success: [], failed: [] };
      }

      setLoading(true);
      setError(null);

      const results = { success: [], failed: [] };

      // Track status for each assignment for UI display
      const statusMap = new Map(); // key: "name|apparatus|judgeIndex" -> status
      assignments.forEach((a) => {
        const key = `${a.name}|${a.apparatus}|${a.judgeIndex}`;
        statusMap.set(key, { ...a, status: 'pending' });
      });

      const notify = (phase, current, total, message) => {
        onProgress?.({
          phase,
          current,
          total,
          message,
          assignments: Array.from(statusMap.values()),
        });
      };

      const updateStatus = (name, apparatusCode, judgeIndex, status) => {
        const key = `${name}|${apparatusCode}|${judgeIndex}`;
        const existing = statusMap.get(key);
        if (existing) {
          statusMap.set(key, { ...existing, status });
        }
      };

      // Build the gender filter for judge search
      const mensFilter = {
        or: [{ mensBrevet: { eq: true } }, { mensOptional: { eq: true } }],
      };
      const womensFilter = {
        or: [
          { womensBrevet: { eq: true } },
          { womensNational: { eq: true } },
          { womensLevel10: { eq: true } },
        ],
      };
      const baseFilter =
        gender === GenderType.FEMALE ? womensFilter : mensFilter;

      try {
        // Get unique judge names to search
        const uniqueNames = [...new Set(assignments.map((a) => a.name.trim()))];

        // Mark all as searching
        assignments.forEach((a) => {
          updateStatus(a.name, a.apparatus, a.judgeIndex, 'searching');
        });
        notify('search', 0, uniqueNames.length, 'Searching for judges...');

        // Step 1: Search for all unique judges in PARALLEL
        const searchPromises = uniqueNames.map(async (trimmedName) => {
          const searchResult = await client.query({
            query: SearchJudges,
            variables: {
              filter: {
                ...baseFilter,
                name: { matchPhrasePrefix: trimmedName },
              },
              limit: 10,
            },
            fetchPolicy: 'network-only',
          });

          let judges = searchResult.data?.searchJudges?.items || [];
          let allCandidates = [...judges];

          // Fallback 1: try last name only with prefix match
          if (judges.length === 0 && trimmedName.includes(' ')) {
            const lastName = trimmedName.split(' ').pop();
            const lastNameResult = await client.query({
              query: SearchJudges,
              variables: {
                filter: {
                  ...baseFilter,
                  name: { matchPhrasePrefix: lastName },
                },
                limit: 10,
              },
              fetchPolicy: 'network-only',
            });
            judges = lastNameResult.data?.searchJudges?.items || [];
            allCandidates = [...judges];
          }

          // Fallback 2: fuzzy search - try multiple strategies to catch typos
          if (judges.length === 0 && trimmedName.includes(' ')) {
            const nameParts = trimmedName.split(' ');
            const firstName = nameParts[0];
            const lastName = nameParts[nameParts.length - 1];

            // Search strategies for typos:
            // 1. Last name ending (catches Aderson->Anderson since both end in "erson")
            // 2. First name (as backup)
            const lastNameEnding =
              lastName.length > 4 ? lastName.slice(-5) : lastName;

            let fuzzyCandidates = [];

            // Try searching by last name ending first
            const endingResult = await client.query({
              query: SearchJudges,
              variables: {
                filter: {
                  ...baseFilter,
                  name: { wildcard: `*${lastNameEnding}*` },
                },
                limit: 30,
              },
              fetchPolicy: 'network-only',
            });
            fuzzyCandidates = endingResult.data?.searchJudges?.items || [];

            // If no results, try first name
            if (fuzzyCandidates.length === 0) {
              const firstNameResult = await client.query({
                query: SearchJudges,
                variables: {
                  filter: {
                    ...baseFilter,
                    name: { wildcard: `*${firstName}*` },
                  },
                  limit: 30,
                },
                fetchPolicy: 'network-only',
              });
              fuzzyCandidates = firstNameResult.data?.searchJudges?.items || [];
            }

            // fuzzyCandidates is already populated from the searches above

            // Simple Levenshtein distance for scoring
            const levenshtein = (a, b) => {
              if (a.length === 0) return b.length;
              if (b.length === 0) return a.length;
              const matrix = [];
              for (let i = 0; i <= b.length; i++) matrix[i] = [i];
              for (let j = 0; j <= a.length; j++) matrix[0][j] = j;
              for (let i = 1; i <= b.length; i++) {
                for (let j = 1; j <= a.length; j++) {
                  if (b.charAt(i - 1) === a.charAt(j - 1)) {
                    matrix[i][j] = matrix[i - 1][j - 1];
                  } else {
                    matrix[i][j] = Math.min(
                      matrix[i - 1][j - 1] + 1,
                      matrix[i][j - 1] + 1,
                      matrix[i - 1][j] + 1
                    );
                  }
                }
              }
              return matrix[b.length][a.length];
            };

            // Score candidates by similarity to input name
            const inputLower = trimmedName.toLowerCase();
            const inputLastNameLower = lastName.toLowerCase();
            const scoredCandidates = fuzzyCandidates.map((j) => {
              const nameLower = j.name.toLowerCase();
              const candidateParts = j.name.split(' ');
              const candidateLastName =
                candidateParts[candidateParts.length - 1]?.toLowerCase() || '';

              // Calculate edit distance for last name (lower is better)
              const lastNameDistance = levenshtein(
                inputLastNameLower,
                candidateLastName
              );
              // Calculate overall name distance
              const fullNameDistance = levenshtein(inputLower, nameLower);

              // Score: inverse of distance (higher is better)
              // Heavily weight last name similarity
              const score = 100 - lastNameDistance * 10 - fullNameDistance;

              return { judge: j, score, lastNameDistance };
            });

            scoredCandidates.sort((a, b) => b.score - a.score);
            allCandidates = scoredCandidates.map((s) => s.judge);

            // Accept if last name edit distance is 2 or less (catches typos like Aderson→Anderson)
            if (
              scoredCandidates.length > 0 &&
              scoredCandidates[0].lastNameDistance <= 2
            ) {
              judges = [scoredCandidates[0].judge];
            }
          }

          // Fallback 3: try first name only if still nothing
          if (
            judges.length === 0 &&
            allCandidates.length === 0 &&
            trimmedName.includes(' ')
          ) {
            const firstName = trimmedName.split(' ')[0];
            if (firstName.length >= 3) {
              const firstNameResult = await client.query({
                query: SearchJudges,
                variables: {
                  filter: {
                    ...baseFilter,
                    name: { matchPhrasePrefix: firstName },
                  },
                  limit: 10,
                },
                fetchPolicy: 'network-only',
              });
              allCandidates = firstNameResult.data?.searchJudges?.items || [];
            }
          }

          // Find best match
          const lowerName = trimmedName.toLowerCase();
          const exactMatch = judges.find(
            (j) => j.name.toLowerCase() === lowerName
          );
          const partialMatch = judges.find((j) =>
            j.name.toLowerCase().includes(lowerName)
          );
          const fuzzyMatch = judges[0]; // Best fuzzy match if available

          const match = exactMatch || partialMatch || fuzzyMatch;

          // Track if matched name differs from input (fuzzy match was used)
          const matchedName = match?.name || null;
          const isExactMatch =
            exactMatch != null ||
            (match && match.name.toLowerCase() === lowerName);

          // If no exact match, provide suggestions (top 3 closest matches)
          let suggestions = [];
          if (!match && allCandidates.length > 0) {
            suggestions = allCandidates.slice(0, 3).map((j) => j.name);
          }

          return {
            name: trimmedName,
            match,
            matchedName,
            isExactMatch,
            suggestions,
            allCandidates,
          };
        });

        const searchResults = await Promise.all(searchPromises);
        const judgeMap = new Map(searchResults.map((r) => [r.name, r.match]));
        const matchedNameMap = new Map(
          searchResults.map((r) => [r.name, r.matchedName])
        );
        const isExactMatchMap = new Map(
          searchResults.map((r) => [r.name, r.isExactMatch])
        );
        const suggestionsMap = new Map(
          searchResults.map((r) => [r.name, r.suggestions || []])
        );
        // Store full candidate objects for UI selection
        const candidatesMap = new Map(
          searchResults.map((r) => [r.name, r.allCandidates || []])
        );

        // Apply user overrides (from previous resolution)
        Object.entries(judgeOverrides).forEach(([name, judge]) => {
          if (judge) {
            judgeMap.set(name, judge);
            matchedNameMap.set(name, judge.name);
            isExactMatchMap.set(name, false); // User-selected, so show as fuzzy
          }
        });

        // Update status for found/not found (with suggestions and matched name)
        assignments.forEach((a) => {
          const match = judgeMap.get(a.name.trim());
          const matchedName = matchedNameMap.get(a.name.trim());
          const isExactMatch = isExactMatchMap.get(a.name.trim());
          const suggestions = suggestionsMap.get(a.name.trim()) || [];
          const candidates = candidatesMap.get(a.name.trim()) || [];
          const key = `${a.name}|${a.apparatus}|${a.judgeIndex}`;
          const existing = statusMap.get(key);
          if (existing) {
            statusMap.set(key, {
              ...existing,
              status: match ? 'found' : 'not_found',
              suggestions: match ? [] : suggestions,
              candidates: match ? [] : candidates,
              matchedName: match ? matchedName : null,
              isExactMatch: isExactMatch,
            });
          }
        });

        const foundCount =
          searchResults.filter((r) => r.match).length +
          Object.keys(judgeOverrides).length;
        const notFoundCount = uniqueNames.length - foundCount;
        const fuzzyMatchCount = searchResults.filter(
          (r) => r.match && !r.isExactMatch
        ).length;
        notify(
          'search',
          uniqueNames.length,
          uniqueNames.length,
          `Found ${foundCount} judges`
        );

        // If searchOnly mode, always pause for user to review before proceeding
        if (searchOnly) {
          const issues = [];
          if (notFoundCount > 0) issues.push(`${notFoundCount} not found`);
          if (fuzzyMatchCount > 0)
            issues.push(`${fuzzyMatchCount} fuzzy matched`);

          const message =
            issues.length > 0
              ? `Review needed: ${issues.join(', ')}`
              : `Found ${foundCount} judges - click Continue to assign`;

          notify('needs_resolution', foundCount, uniqueNames.length, message);
          setLoading(false);
          return {
            success: [],
            failed: [],
            needsResolution: true,
            searchResults,
            judgeMap,
            statusMap,
          };
        }

        // Step 2: Clean up existing assignments for these judges (removes duplicates)
        if (!dryRun) {
          notify('cleanup', 0, 1, 'Cleaning up old assignments...');
          const matchedJudgeIds = new Set(
            searchResults.filter((r) => r.match).map((r) => r.match.id)
          );
          const existingAssignments =
            session?.judgeAssignments?.items ||
            session?.sessionJudgeAssignments?.items ||
            [];
          const toRemove = existingAssignments.filter(
            (a) => matchedJudgeIds.has(a.judgeId) && !a._deleted
          );

          if (toRemove.length > 0) {
            await Promise.all(
              toRemove.map((a) =>
                removeJudge(a.id, a._version, {
                  skipUpdate: true,
                }).catch(() => {})
              )
            );
          }
          notify('cleanup', 1, 1, `Cleaned ${toRemove.length} old assignments`);
        }

        // Step 3: Assign judges in PARALLEL
        notify('assign', 0, assignments.length, 'Assigning judges...');

        let completed = 0;
        const assignPromises = assignments.map(async (assignment) => {
          const { name, apparatus: apparatusCode, judgeIndex } = assignment;
          const judge = judgeMap.get(name.trim());

          if (!judge) {
            results.failed.push({ name, reason: 'Judge not found' });
            completed++;
            notify(
              'assign',
              completed,
              assignments.length,
              `${name}: not found`
            );
            return;
          }

          try {
            updateStatus(name, apparatusCode, judgeIndex, 'assigning');
            notify(
              'assign',
              completed,
              assignments.length,
              `Assigning ${name}...`
            );

            const scoreType =
              judgePanel?.[judgeIndex]?.type || `J${judgeIndex + 1}`;

            if (dryRun) {
              // Simulate delay for UI testing
              await new Promise((r) => setTimeout(r, 300));
            } else {
              // Skip individual updates - we'll do one refetch at the end
              await assignJudge(judge, scoreType, apparatusCode, {
                skipUpdate: true,
              });
            }

            const apparatusName =
              apparatusCodeToName[apparatusCode] || apparatusCode;
            results.success.push({ name, apparatus: apparatusName, scoreType });

            updateStatus(name, apparatusCode, judgeIndex, 'assigned');
            completed++;
            notify(
              'assign',
              completed,
              assignments.length,
              `${name} → ${apparatusCode}${judgeIndex + 1}`
            );
          } catch (err) {
            results.failed.push({ name, reason: err.message });
            updateStatus(name, apparatusCode, judgeIndex, 'error');
            completed++;
            notify('assign', completed, assignments.length, `${name}: error`);
          }
        });

        await Promise.all(assignPromises);
        notify(
          'done',
          assignments.length,
          assignments.length,
          dryRun ? 'Simulation complete!' : 'Complete!'
        );

        // DEBUG: Log what was assigned
        console.log('=== BULK ASSIGN COMPLETE ===');
        console.log('Success:', results.success);
        console.log('Failed:', results.failed);

        // Refetch the session query to update UI with new judge data
        // Evict stale data first, then refetch to ensure clean state
        if (!dryRun && session?.id) {
          try {
            // Small delay for DynamoDB eventual consistency
            await new Promise((r) => setTimeout(r, 500));

            // Evict all Stage objects to force fresh data
            const rotations = session.rotations?.items || [];
            for (const rotation of rotations) {
              for (const stage of rotation.stages?.items || []) {
                client.cache.evict({
                  id: client.cache.identify({
                    __typename: 'Stage',
                    id: stage.id,
                  }),
                });
              }
            }
            client.cache.gc();

            // Now refetch the session with fresh data from server
            console.log('=== REFETCHING SESSION ===');
            const refetchResult = await client.refetchQueries({
              include: [GetFullSession],
            });
            console.log('Refetch result:', refetchResult);

            // DEBUG: Log judges in each stage after refetch
            const newSession = client.cache.readQuery({
              query: GetFullSession,
              variables: { id: session.id },
            });
            console.log('=== JUDGES AFTER REFETCH ===');
            newSession?.getSession?.rotations?.items?.forEach((rotation) => {
              rotation?.stages?.items?.forEach((stage) => {
                const judgeCount = stage?.judges?.items?.length || 0;
                if (judgeCount > 0) {
                  console.log(
                    `Stage ${stage.apparatus}:`,
                    stage.judges.items.map((j) => ({
                      scoreTypes: j.scoreTypes,
                      judgeName: j.sessionJudgeAssignment?.judge?.name,
                    }))
                  );
                }
              });
            });
          } catch (refetchError) {
            console.warn(
              'Refetch failed, UI may need manual refresh:',
              refetchError
            );
          }
        }

        return results;
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [session, gender, client, assignJudge, removeJudge]
  );

  return {
    assignJudge,
    removeJudge,
    bulkAssignJudges,
    loading,
    error,
  };
}

export default useJudgeAssignment;
